/**
 * ./exercise5/Exercise5Test.js
 *
 * This module presents **a memory test for Exercise 5**. Users must **identify the 2 sentences**
 * that were **displayed during the workout** from a **list of 4 randomized options**.
 * It is the first time to have test at the end of Exercise!
 *
 * - **Randomized sentence selection**:
 *   - Mixes **2 displayed sentences** with **2 unseen sentences**.
 *   - **Shuffles the sentences** to prevent memorization bias.
 *
 * - **User Selection & Feedback**:
 *   - Users **click on sentences** to **make selections**.
 *   - **Correct/Wrong indicators appear** upon selection.
 *   - Users **must identify exactly 2 correct sentences**.
 *
 * - **Performance Tracking & Accuracy Calculation**:
 *   - **Counts total user clicks**.
 *   - **Calculates accuracy** → `(2 / totalClicks) * 100`.
 *   - **Navigates to `Exercise5Finish` after selection**.
 *
 */


import React, { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './Exercise5Test.css';
import correctIcon from './icons/correcticon.svg';
import wrongIcon from './icons/wrongicon.svg';

function Exercise5Test() {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [results, setResults] = useState({});
  const [totalClicks, setTotalClicks] = useState(0);
  const duration = location.state?.duration || 10; // Get duration from navigation state
  const errorRate = location.state?.errorRate;
  
  const { selectedDisplayedSentences = [], selectedUnusedSentences = [] } = location.state || {};
  console.log('Sentences:', { selectedDisplayedSentences, selectedUnusedSentences });
  
  const allSentences = [...selectedDisplayedSentences, ...selectedUnusedSentences];
  console.log('All sentences:', allSentences);

  // Shuffle the options to randomize their order
  const shuffleArray = (array) => {
    return [...array].sort(() => Math.random() - 0.5);
  };

  // Move shuffleArray call into a useMemo or useEffect to ensure it only runs once
  const [options] = useState(() => shuffleArray(allSentences));

  const handleSelection = (sentence) => {
    // Only add the selection if it hasn't been selected yet
    if (!selectedAnswers.includes(sentence)) {
      // Increment total clicks first
      const newTotalClicks = totalClicks + 1;
      setTotalClicks(newTotalClicks);
      
      const newSelectedAnswers = [...selectedAnswers, sentence];
      setSelectedAnswers(newSelectedAnswers);
      
      const wasDisplayed = selectedDisplayedSentences.includes(sentence);
      setResults(prev => ({
        ...prev,
        [sentence]: wasDisplayed
      }));
      
      // Check if we have exactly 2 correct answers
      const correctAnswers = newSelectedAnswers.filter(ans => 
        selectedDisplayedSentences.includes(ans)
      );
      
      if (correctAnswers.length === 2) {
        // Calculate accuracy using the updated total clicks
        const accuracy = (2 / newTotalClicks) * 100;
        setTimeout(() => {
          navigate('/exercise5finish', {
            state: { 
              accuracy,
              timeSpent: 0,
              duration: duration,
              errorRate: errorRate
            }
          });
        }, 1500);
      }
    }
  };

  return (
    <div className="exercise5-test-container">
      <h1>Which 2 sentences did you see in this exercise?</h1>
      {/* <div style={{ position: 'absolute', top: '10px', left: '10px', background: '#f0f0f0', padding: '5px', borderRadius: '5px' }}>
        Debug: Clicks: {totalClicks}, Accuracy: {totalClicks > 0 ? ((2 / totalClicks) * 100).toFixed(1) : 0}%
      </div> */}
      {options.length === 0 ? (
        <p>No sentences available</p>
      ) : (
        <div className="sentences-list">
          {options.map((sentence, index) => (
            <div 
              key={index} 
              className={`sentence-item ${selectedAnswers.includes(sentence) ? 'selected' : ''}`}
              onClick={() => !selectedAnswers.includes(sentence) && handleSelection(sentence)}
            >
              {sentence}
              {selectedAnswers.includes(sentence) && (
                <img 
                  src={results[sentence] ? correctIcon : wrongIcon} 
                  alt={results[sentence] ? "Correct" : "Wrong"}
                  className="result-icon"
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Exercise5Test;
